import React, { useState } from "react";
import insights from "../../data/insights.json";

const PARCHMENT = "brightness(0) saturate(100%) invert(97%) sepia(8%) saturate(300%) hue-rotate(330deg) brightness(102%)";

function getWeekLabel() {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  return monday.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function formatDate(iso) {
  const [y, m, d] = iso.split("-");
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });
}

const InsightCard = ({ insight }) => {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: "#FAF6EF",
        borderLeft: "2px solid #C8A95A",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.3s ease",
        boxShadow: hovered
          ? "0 12px 40px rgba(44,10,10,0.14), 0 2px 8px rgba(44,10,10,0.08)"
          : "0 2px 8px rgba(44,10,10,0.05)",
      }}
      data-testid={`insight-card-${insight.id}`}
    >
      {/* Image or fallback */}
      <div className="relative overflow-hidden" style={{ height: 220, flexShrink: 0 }}>
        {insight.imageUrl ? (
          <>
            <img
              src={insight.imageUrl}
              alt=""
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                transition: "transform 0.5s ease",
                transform: hovered ? "scale(1.04)" : "scale(1)",
              }}
            />
            {/* Gradient scrim for tag legibility */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(to top, rgba(10,6,4,0.62) 0%, transparent 55%)",
              }}
            />
          </>
        ) : (
          /* Fallback — dark wine bg + emblem */
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#2C0A0A",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="/HNMB_logo_.svg"
              alt=""
              style={{ width: 72, height: "auto", filter: PARCHMENT, opacity: 0.55 }}
            />
          </div>
        )}

        {/* Category tag — bottom-left of image */}
        <span
          style={{
            position: "absolute",
            bottom: 12,
            left: 14,
            fontFamily: "'Jost', sans-serif",
            fontSize: 10,
            fontWeight: 400,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#FAF6EF",
            backgroundColor: "rgba(44,10,10,0.72)",
            padding: "4px 10px",
            backdropFilter: "blur(4px)",
          }}
        >
          {insight.category}
        </span>
      </div>

      {/* Card body */}
      <div style={{ padding: "20px 22px 24px", display: "flex", flexDirection: "column", flex: 1 }}>
        {/* Date */}
        <p
          style={{
            fontFamily: "'Jost', sans-serif",
            fontSize: 11,
            fontWeight: 200,
            letterSpacing: "0.22em",
            color: "#8C7E6A",
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          {formatDate(insight.date)}
          {insight.market && (
            <span style={{ marginLeft: 12, color: "#C8A95A" }}>· {insight.market}</span>
          )}
        </p>

        {/* Headline */}
        <h3
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 22,
            fontWeight: 600,
            lineHeight: 1.25,
            color: "#2C0A0A",
            marginBottom: 12,
          }}
        >
          {insight.title}
        </h3>

        {/* Body — 3-line clamp with fade, expanded on Read More */}
        <div style={{ position: "relative", flex: 1 }}>
          <p
            style={{
              fontFamily: "'Libre Baskerville', Georgia, serif",
              fontSize: 14,
              lineHeight: 1.85,
              color: "#1C1410",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: expanded ? "unset" : 3,
              overflow: expanded ? "visible" : "hidden",
            }}
          >
            {insight.body}
          </p>
          {!expanded && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: 32,
                background: "linear-gradient(to bottom, transparent, #FAF6EF)",
                pointerEvents: "none",
              }}
            />
          )}
        </div>

        {/* Expanded read-more text */}
        {expanded && insight.readMore && (
          <p
            style={{
              fontFamily: "'Libre Baskerville', Georgia, serif",
              fontSize: 13,
              lineHeight: 1.85,
              color: "#3A3A3A",
              marginTop: 14,
              paddingTop: 14,
              borderTop: "1px solid rgba(200,169,90,0.25)",
            }}
          >
            {insight.readMore}
          </p>
        )}

        {/* Read More button */}
        {(insight.readMore || insight.readMoreUrl) && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            style={{
              marginTop: 16,
              fontFamily: "'Jost', sans-serif",
              fontSize: 10,
              fontWeight: 400,
              letterSpacing: "0.36em",
              textTransform: "uppercase",
              color: "#C8A95A",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              alignSelf: "flex-start",
            }}
          >
            {expanded ? "Collapse ↑" : "Read More →"}
          </button>
        )}

        {/* Image credit */}
        {insight.imageCredit && (
          <p
            style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: 9,
              letterSpacing: "0.2em",
              color: "#B0A090",
              marginTop: 16,
              textTransform: "uppercase",
            }}
          >
            {insight.imageCredit}
          </p>
        )}
      </div>
    </article>
  );
};

const IntelligenceFeed = () => (
  <section
    style={{ backgroundColor: "#FAF6EF", padding: "80px 0 96px" }}
    data-testid="intelligence-feed"
  >
    <div style={{ maxWidth: 1340, margin: "0 auto", padding: "0 24px" }}>

      {/* Section header */}
      <div style={{ marginBottom: 48 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 6 }}>
          <span style={{ display: "block", width: 40, height: 1, backgroundColor: "#C8A95A" }} />
          <p
            style={{
              fontFamily: "'Jost', sans-serif",
              fontSize: 11,
              fontWeight: 400,
              letterSpacing: "0.4em",
              textTransform: "uppercase",
              color: "#C8A95A",
              margin: 0,
            }}
          >
            Numbardar Intelligence
          </p>
        </div>
        <p
          style={{
            fontFamily: "'Jost', sans-serif",
            fontSize: 10,
            fontWeight: 200,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: "#8C7E6A",
            marginLeft: 56,
          }}
        >
          Week of {getWeekLabel()}
        </p>
      </div>

      {/* Heading */}
      <h2
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: "clamp(28px, 3.5vw, 44px)",
          fontWeight: 500,
          color: "#0A0A0A",
          lineHeight: 1.1,
          maxWidth: 600,
          marginBottom: 48,
        }}
      >
        Market intelligence,{" "}
        <em style={{ fontStyle: "italic", fontWeight: 400, color: "#0A3728" }}>
          for those who act before the market.
        </em>
      </h2>

      {/* Cards grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))",
          gap: 28,
        }}
      >
        {insights.map(insight => (
          <InsightCard key={insight.id} insight={insight} />
        ))}
      </div>

      {/* Disclaimer */}
      <p
        style={{
          fontFamily: "'Jost', sans-serif",
          fontSize: 9,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          color: "#8C7E6A",
          textAlign: "center",
          marginTop: 48,
        }}
      >
        Insights reflect Numbardar's independent market assessment · Not investment advice
      </p>
    </div>
  </section>
);

export default IntelligenceFeed;
